/* eslint-disable max-len */
import { Grid } from '@material-ui/core';
import { Cached } from '@material-ui/icons';
import { Input, OrganizationSelect, SalesUserSelect, Select } from 'components/forms';
import { autobind } from 'core-decorators';
import { parseQueryString, toQueryString } from 'core/utils';
import orderCategoriesQuery from 'graphql/queries/order-categories.gql';
import orderStatusQuery from 'graphql/queries/order-status.gql';
import organizationCategoriesQuery from 'graphql/queries/organization-categories.gql';
import regionsQuery from 'graphql/queries/regions.gql';
import seasonsQuery from 'graphql/queries/seasons.gql';
import shipmentStatusQuery from 'graphql/queries/shipment-status.gql';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { compose, graphql, withApollo } from 'react-apollo';
import styles from './styles.css';

const buildOptions = (items, emptyOption) => {
    const options = items.map(({id, name}) => ({value: id, label: name}));
    if (emptyOption) options.unshift({value: '', label: emptyOption});
    return options;
};

@compose(graphql(seasonsQuery, {
    name: 'seasonsData',
    skip: props => !props.filters.includes('season'),
}))
@withApollo
@autobind
export default class Filters extends Component {
    static propTypes = {
        filters: PropTypes.arrayOf(PropTypes.oneOf([
            'season',
            'region',
            'salesUser',
            'organizationCategory',
            'organization',
            'orderCategory',
            'orderStatus',
            'shipmentStatus',
            'id',
        ])).isRequired,
        onChange: PropTypes.func.isRequired,
        disabled: PropTypes.bool,
    };

    static contextTypes = {
        router: PropTypes.object,
    };

    state = {
        loading: false,
        cascade: [],
        filters: {},
    };

    componentDidMount() {
        const {route} = this.context.router;
        this.load(route)
        const params = this.populateFilters({props: this.props, location: route.location});
        this.props.onChange(params, this.filters);
    }

    componentWillReceiveProps(nextProps, nextContext) {
        const {route} = nextContext.router;
        const params = this.populateFilters({props: nextProps, location: route.location});
        const stringifyFilters = props => Object.keys(this.getDefaultFilters(props)).join('-');
        if (stringifyFilters(this.props) !== stringifyFilters(nextProps)) this.props.onChange(params);
        this.load(route, this.filters);
    }

    filters = [
        {
            name: 'organizationCategories',
            nameSing: 'organizationCategory',
            query: organizationCategoriesQuery,
            skip: !this.props.filters.includes('organizationCategory'),
        },
        {
            name: 'regions',
            nameSing: 'region',
            query: regionsQuery,
            skip: !this.props.filters.includes('region'),
        },
        {
            name: 'orderCategories',
            nameSing: 'orderCategory',
            query: orderCategoriesQuery,
            skip: !this.props.filters.includes('orderCategory'),
        },
        {
            name: 'orderStatus',
            nameSing: 'orderStatus',
            query: orderStatusQuery,
            skip: !this.props.filters.includes('orderStatus'),
        },
        {
            name: 'shipmentStatus',
            nameSing: 'shipmentStatus',
            query: shipmentStatusQuery,
            skip: !this.props.filters.includes('shipmentStatus'),
        },
    ]

    async load(route, filters) {
        const params = parseQueryString(route.location.search) || {};
        this.setState({loading: true});

        const variables = {};
        this.state.cascade.forEach(elem => {
            variables[elem] = params[elem];
        })

        if (filters) {
            await Promise.all(filters.map(filter => {
                if (!filter.skip) {
                    return this.loadResponse(params, filter, variables)
                }
            }));
            this.setState({loading: false});
        }
    }

    async loadResponse(params, filter, variables) {
        const response = await this.makeRequest(params, filter, variables);
        this.setState({
            [filter.name]: response.data && response.data[filter.name],
        });
        return response;
    }

    async makeRequest(params, filter, variables) {
        const paramsToSend = this.getParamsToSend(params, filter, variables)
        return await this.props.client.query({
            query: filter.query,
            variables: {
                season: paramsToSend.season || undefined,
                region: paramsToSend.region || undefined,
                salesUser: paramsToSend.salesUser || undefined,
                organizationCategory: paramsToSend.organizationCategory || undefined,
                organization: paramsToSend.organization || undefined,
            },
            fetchPolicy: 'no-cache',
        });
    }

    getAutocompleteVariables(filter) {
        const {route} = this.context.router;
        const params = parseQueryString(route.location.search) || {};
        const variables = {};
        this.state.cascade.forEach(elem => {
            variables[elem] = params[elem]
        });
        const autocompleteVariables = this.getParamsToSend(params, filter, variables);
        if (filter.name === 'organizations') {
            autocompleteVariables.category = autocompleteVariables.organizationCategory;
            delete autocompleteVariables.organizationCategory;
        }
        return autocompleteVariables;
    }

    getParamsToSend(params, filter, variables) {
        if (filter.name !== 'seasons') {
            const index = this.state.cascade.indexOf(filter.nameSing || filter.name);
            if (index === -1) {
                return {...params};
            }

            if (index !== 0) {
                const paramsToSend = {};
                for (let i = 0 ; i <= index; i++) {
                    paramsToSend[this.state.cascade[i]] = variables[this.state.cascade[i]]
                }
                return paramsToSend;
            }
        }

        return {};
    }

    populateFilters({props, location}) {
        const filters = {
            ...this.getDefaultFilters(props),
            ...this.getQuerystringFilters(location),
        };
        this.setState({filters: filters});
        return filters;
    }

    getDefaultFilters(props) {
        const filters = {};
        if (props.filters.includes('season') && !props.seasonsData.loading) {
            const activeSeason = props.seasonsData.seasons.find(({isActive}) => Boolean(isActive));
            if (activeSeason) filters.season = activeSeason.id;
        }
        return filters;
    }

    getQuerystringFilters(location) {
        const filters = parseQueryString(location.search);
        Object.keys(filters).forEach(key => {
            if (!Number.isNaN(Number(filters[key]))) filters[key] = Number(filters[key]);
        });
        return filters;
    }

    isVisible(filter, options) {
        return this.props.filters.includes(filter) && options && options.length > 0;
    }

    setFiltersInQueryString(filters) {
        const {history} = this.context.router;
        history.replace({search: toQueryString(filters)});
    }

    getFieldProps(key, fieldsToClean = []) {
        return {
            value: this.state['filters'][key] || '',
            onChange: this.handleChange(key, fieldsToClean),
            disabled: this.props.disabled,
        };
    }

    handleChange(key, fieldsToClean) {
        return value => {
            const cascade = [...this.state.cascade];
            if (!cascade.includes(key)) cascade.push(key);
            const newState = {filters: {...this.state.filters, [key]: value}, cascade: [...cascade]};

            fieldsToClean.forEach(field => {
                newState.filters[field] = '';
            });

            this.setState(newState, () => {
                this.setFiltersInQueryString(this.state);
                this.props.onChange(this.state.filters);
            });
        };
    }

    handleReset() {
        this.setState({
            cascade: [],
            filters: {
                region: undefined,
                salesUser: undefined,
                organization: undefined,
                organizationCategory: undefined,
                orderCategory: undefined,
                orderStatus: undefined,
                shipmentStatus: undefined,
                season: undefined,
                page: undefined,
                id: undefined,
            },
            ...this.getDefaultFilters(this.props),
        }, () => {
            this.setFiltersInQueryString(this.state.filters);
            this.props.onChange(this.state.filters);
        });
    }

    renderFilter(name) {
        return this.isVisible(name, this.state[name]);
    }

    render() {
        const {seasonsData} = this.props;
        const loading = (seasonsData && seasonsData.loading);
        if (loading) return null;

        const salesUserVariables = this.getAutocompleteVariables({nameSing: 'salesUser', name: 'salesUser'});
        const organizationVariables = this.getAutocompleteVariables({nameSing: 'organization', name: 'organizations'});

        return (
            <Grid container spacing={1} className={styles.root} justify='center' style={{marginBottom: this.props.filters.length > 5 ? '0rem' : '8rem'}}>
                {this.props.filters.includes('id') && (
                    <Grid item xs={12} md={2} >
                        <div>
                            <p style={{paddingBottom: '10px'}}>Número de pedido</p>
                            <Input
                                type='number'
                                placeholder='Ingresar número'
                                {...this.getFieldProps(
                                    'id',
                                    ['region', 'salesUser', 'organizationCategory', 'organization',
                                        'shipmentStatus', 'orderCategory', 'orderStatus'],
                                )}
                            />
                        </div>
                    </Grid>
                )}
                {seasonsData && this.isVisible('season', seasonsData.seasons) && (
                    <Grid item xs={12} md={2} container justifyContent='space-between' direction='column'>
                        <p style={{paddingBottom: '10px'}}>Campaña</p>
                        <Select
                            options={buildOptions(seasonsData.seasons)}
                            {...this.getFieldProps('season')}
                        />
                    </Grid>
                )}
                {this.isVisible('region', this.state['regions']) && (
                    <Grid item xs={12} md={2} container justifyContent='space-between' direction='column'>
                        <p style={{paddingBottom: '10px'}}>Región</p>
                        <Select
                            options={buildOptions(this.state['regions'], 'Todas')}
                            {...this.getFieldProps('region', ['id'])}
                        />
                    </Grid>
                )}
                {this.props.filters.includes('salesUser') && (
                    <Grid item xs={12} md={2} container justifyContent='space-between' direction='column'>
                        <p style={{paddingBottom: '10px'}}>Comercial</p>
                        <SalesUserSelect
                            key={JSON.stringify(salesUserVariables)}
                            {...this.getFieldProps('salesUser', ['id'])}
                            variables={salesUserVariables}
                        />
                    </Grid>
                )}
                {this.isVisible('organizationCategory', this.state['organizationCategories']) && (
                    <Grid item xs={12} md={2} container direction='column'>
                        <p style={{paddingBottom: '10px'}}>Tipo de Organización</p>
                        <Select
                            options={buildOptions(this.state['organizationCategories'], 'Todas')}
                            {...this.getFieldProps('organizationCategory', ['id'])}
                        />
                    </Grid>
                )}
                {this.props.filters.includes('organization') && (
                    <Grid item xs={12} md={2} container direction='column'>
                            <p style={{paddingBottom: '10px'}}>Organización</p>
                        <OrganizationSelect
                            key={JSON.stringify(organizationVariables)}
                            {...this.getFieldProps('organization', ['id'])}
                            variables={organizationVariables}
                        />
                    </Grid>
                )}
                {this.isVisible('orderCategory', this.state['orderCategories']) && (
                    <Grid item xs={12} md={2} >
                        <p style={{paddingBottom: '10px'}}>Tipo de pedido</p>
                        <Select
                            options={buildOptions(this.state.orderCategories, 'Todos')}
                            {...this.getFieldProps('orderCategory', ['id'])}
                        />
                    </Grid>
                )}
                {this.isVisible('orderStatus', this.state['orderStatus']) && (
                    <Grid item xs={12} md={2} >
                        <p style={{paddingBottom: '10px'}}>Estado de pedido</p>
                        <Select
                            options={buildOptions(this.state.orderStatus, 'Todos')}
                            {...this.getFieldProps('orderStatus', ['id'])}
                        />
                    </Grid>
                )}
                {this.isVisible('shipmentStatus', this.state['shipmentStatus']) && (
                    <Grid item xs={12} md={2} >
                        <p style={{paddingBottom: '10px'}}>Estado de envío</p>
                        <Select
                            options={buildOptions(this.state.shipmentStatus, 'Todos')}
                            {...this.getFieldProps('shipmentStatus', ['id'])}
                        />
                    </Grid>
                )}
                <Grid item xs={12} md={2} className={styles.resetButton} onClick={this.handleReset}>
                    <p style={{paddingBottom: '10px'}} className={styles.resetButtonItems} style={{fontSize: '12px'}} >Limpiar Filtros</p>
                    <Cached className={styles.resetButtonItems} fontSize='18px' />
                </Grid>
            </Grid>
        );
    }
}



// WEBPACK FOOTER //
// ./components/filters/index.js